/**
 * Logo & Branding Page
 * Brand color settings and logo/cover image uploads
 */

import { createFileRoute, Link, useParams } from '@tanstack/react-router';
import { Helmet } from 'react-helmet-async';
import { ExternalLink, Settings } from 'lucide-react';
import { Button, SettingsPageHeader } from '@/components/ui';
import { BusinessSetupPageShell } from './business-setup/components/business-setup-page-shell';
import { BrandColorCard } from './business-setup/components/brand-color-card';
import { LogoCoverImageCard } from './business-setup/components/logo-cover-image-card';
import { useGetOrganizationSettings } from '@/api/generated/organization/organization';
import { getOrganizationSettingsOrgQueryKey } from '@/api/organization-query-keys';
import { useOnlineBookingSettings } from './online-booking/hooks/use-online-booking-settings';
import { formatDocumentTitle } from '@/lib/document-title';
import { salonBooksOutsideHayven } from '@/features/salon-setup/salon-setup-page-meanings';
import { useSalonSetup } from '@/hooks/use-salon-setup';
import { usePublishedBioPageUrl } from './business-setup/components/use-published-bio-page-url';

export const Route = createFileRoute('/_auth/$orgId/settings/business-setup/logo-branding')({
  component: LogoBrandingPage,
});

function LogoBrandingPage() {
  const { orgId } = useParams({ from: '/_auth/$orgId/settings/business-setup/logo-branding' });
  const { data: settingsResponse, isLoading } = useGetOrganizationSettings({
    query: { queryKey: getOrganizationSettingsOrgQueryKey(orgId) },
  });
  const { data: onlineBookingResponse } = useOnlineBookingSettings();
  // With Calendar off there is no Hayven booking page (it is paused), and a restaurant never has one: the salon's client
  // page is the Bio Page.
  const booksOutsideHayven = salonBooksOutsideHayven(useSalonSetup()) || onlineBookingResponse?.data?.bookingAction === 'ORDER';
  const publishedBioUrl = usePublishedBioPageUrl(booksOutsideHayven);
  const bookingBaseUrl = import.meta.env.VITE_BOOKING_BASE_URL || 'https://booking.hayven.ai';
  const bookingSlug = onlineBookingResponse?.data?.bookingSlug;
  const bookingUrl = bookingSlug ? `${bookingBaseUrl}/${bookingSlug}` : null;
  const previewButtonClassName =
    'h-11 rounded-[var(--r-pill)] bg-clovie-primary px-5 text-[16px] font-semibold text-white shadow-soft hover:bg-clovie-primary-hover max-sm:w-11 max-sm:px-0';

  return (
    <>
      <Helmet defer={false}>
        <title>{formatDocumentTitle('Logo & Branding')}</title>
      </Helmet>

      <BusinessSetupPageShell orgId={orgId} activePath="logo-branding">
        {/* Page Header with Preview Button below (icon-only beside the title on phones) */}
        <SettingsPageHeader
          actionBesideTitleOnPhone
          title="Logo & Branding"
          description="Customize your client page with your brand color, logo, and cover image."
          actionButton={
            booksOutsideHayven ? (
              <Button asChild className={previewButtonClassName}>
                {publishedBioUrl ? (
                  <a href={publishedBioUrl} target="_blank" rel="noopener noreferrer">
                    Preview Bio Page
                    <ExternalLink className="h-4 w-4" />
                  </a>
                ) : (
                  // Not live yet: the editor's preview shows it with these colors and images.
                  <Link to="/$orgId/marketing/bio-page" params={{ orgId }}>
                    Preview Bio Page
                  </Link>
                )}
              </Button>
            ) : bookingUrl ? (
              <Button asChild className={previewButtonClassName}>
                <a
                  href={bookingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Preview Client Page"
                >
                  <span className="max-sm:hidden">Preview Client Page</span>
                  <ExternalLink className="h-4 w-4" />
                </a>
              </Button>
            ) : (
              <Button asChild className={previewButtonClassName}>
                <Link
                  to="/$orgId/settings/online-booking/setup-integration"
                  params={{ orgId }}
                  aria-label="Set up client page"
                >
                  <span className="max-sm:hidden">Set up client page</span>
                  <Settings className="h-4 w-4 sm:hidden" />
                </Link>
              </Button>
            )
          }
        />

        <div className="max-w-2xl space-y-6">
          {isLoading ? (
            <div
              className="rounded-[var(--r-2xl)] border border-clovie-border-soft bg-clovie-surface p-6 shadow-soft"
              role="status"
              aria-label="Loading branding settings"
            >
              <div className="h-5 w-44 animate-pulse rounded-[var(--r-sm)] bg-clovie-sunk" />
              <div className="mt-5 h-4 w-full animate-pulse rounded-[var(--r-sm)] bg-clovie-sunk" />
              <div className="mt-3 h-4 w-2/3 animate-pulse rounded-[var(--r-sm)] bg-clovie-sunk" />
              <span className="sr-only">Loading branding settings...</span>
            </div>
          ) : (
            <>
              <BrandColorCard brandColor={settingsResponse?.data?.brandColor} />
              <LogoCoverImageCard
                logoUrl={settingsResponse?.data?.logoUrl}
                coverImageUrl={settingsResponse?.data?.coverImageUrl}
                bookingsOutsideHayven={booksOutsideHayven}
              />
            </>
          )}
        </div>
      </BusinessSetupPageShell>
    </>
  );
}
